/**
 * EvoBillingCyclesModal Component
 * 
 * A wrapper component that renders the BillingCyclesModal with EVO Design System
 * styling using Tailwind CSS scoped to the EvoWrapper component.
 * 
 * This component is designed to replace the existing "Update/Review Billing Cycles"
 * modal on the Contract Details page when activated by a feature flag.
 * 
 * It uses existing Pidget UI hooks (useGetBillingCycles, useGetContractDetails)
 * and transforms the data into the internal format expected by the Evo components.
 */

import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { EvoWrapper } from '@/components/evo/EvoWrapper';
import { BillingCyclesModal } from './BillingCyclesModal';
import { BillingCyclesModalData, ContractLine, BillingCycle } from './types';
import { useSelectedLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import useGetBillingCycles from '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useGetBillingCycles';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import useUpdateBillingCadence from
  '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useUpdateBillingCadence';
import { toMutationDateString } from '@/utils/date.utils';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

interface EvoBillingCyclesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Transforms Pidget UI billing cycle data from the GraphQL API into the internal
 * Evo BillingCycle format expected by the ported components.
 */
function transformBillingCycle(apiCycle: any): BillingCycle {
  return {
    billingCycleID: apiCycle.billingCycleID || '',
    contractID: apiCycle.contractID || '',
    contractLineID: apiCycle.contractLineID || '',
    startDate: apiCycle.startDate || '',
    endDate: apiCycle.endDate || '',
    billingDate: apiCycle.billingDate || '',
    processingStatus: apiCycle.processingStatus || '',
    statusReason: apiCycle.statusReason || null,
    consolidationCriteriaCode: apiCycle.consolidationCriteriaCode || '',
    billingCustomerAccountNumber: apiCycle.billingCustomerAccountNumber || '',
    billingCustomerAccountName: apiCycle.billingCustomerAccountName || '',
    contractNumber: apiCycle.contractNumber || '',
    stockCode: apiCycle.stockCode || '',
    stockName: apiCycle.stockName || '',
    quantity: apiCycle.quantity || null,
    price: apiCycle.price || null,
    net: apiCycle.net || null,
    salesOrderNumber: apiCycle.salesOrderNumber || null,
    salesOrderStatus: apiCycle.salesOrderStatus || null,
    billingValue: parseFloat(apiCycle.billingValue) || 0,
    currency: apiCycle.currency || 'GBP',
    soCount: apiCycle.soCount ?? null,
    latestSalesOrderId: apiCycle.latestSalesOrderId ?? null,
    firstSalesOrderId: apiCycle.firstSalesOrderId ?? null,
  };
}

/**
 * Builds the BillingCyclesModalData from Pidget UI data sources.
 * Groups billing cycles by contract line ID and creates ContractLine objects.
 */
function buildEvoBillingData(
  billingCyclesFromApi: any[],
  contract: any | undefined,
  selectedLineRows: any[],
  cachedBCLines: any,
): BillingCyclesModalData {
  // Group billing cycles by contractLineID
  const groupedByLineId = billingCyclesFromApi.reduce<Record<string, BillingCycle[]>>(
    (storage, apiCycle) => {
      if (!apiCycle) return storage;
      const key = apiCycle.contractLineID;
      if (!storage[key]) {
        storage[key] = [];
      }
      storage[key].push(transformBillingCycle(apiCycle));
      return storage;
    },
    {}
  );

  // Build a lookup for lineBillingStatus and pause dates from cached lines
  const bcItems = cachedBCLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
  const lineBillingStatusMap: Record<string, string> = {};
  const linePauseStartDateMap: Record<string, string | null> = {};
  const linePauseEndDateMap: Record<string, string | null> = {};
  bcItems.forEach((line: any) => {
    if (line?.id && line?.lineBillingStatus) {
      lineBillingStatusMap[line.id] = line.lineBillingStatus;
      linePauseStartDateMap[line.id] = line.lineBillingCurrentStatusStartDate || null;
      linePauseEndDateMap[line.id] = line.lineBillingCurrentStatusEndDate || null;
    }
  });

  // Create ContractLine objects from selected rows with their billing cycles
  const selectedLines: ContractLine[] = selectedLineRows.map((row) => {
    const contractLine = row.payload?.contractLine;
    const lineId = row.id;
    const cycles = groupedByLineId[lineId] || [];

    return {
      id: lineId,
      contractId: contractLine?.contractId || '',
      stockCode: contractLine?.stockCode || cycles[0]?.stockCode || '',
      stockName: contractLine?.stockName || cycles[0]?.stockName || '',
      status: contractLine?.status || 'Active',
      lineBillingStatus: lineBillingStatusMap[lineId] || null,
      lineBillingCurrentStatusStartDate: linePauseStartDateMap[lineId] || null,
      lineBillingCurrentStatusEndDate: linePauseEndDateMap[lineId] || null,
      BillingCycles: cycles,
    };
  });

  return {
    selectedLines,
    customerNumber: contract?.customer?.customerAccountNumber || '',
    contractNumber: contract?.contractNumber || '',
  };
}

export function EvoBillingCyclesModal({
  open,
  onOpenChange,
}: EvoBillingCyclesModalProps) {
  // Get contract ID from route params
  const { contractId }: { contractId: UUID } = useParams();

  // Use existing Pidget UI hooks
  const selectedLineRows = useSelectedLines();
  const { getBillingCycles, refetchBillingCycles, billingCycles, isLoadingBillingCycles } = useGetBillingCycles();
  const { contract, isContractLoading } = useGetContractDetails(contractId);
  const { updateBillingCadence, isLoadingUpdateBillingCadence } = useUpdateBillingCadence(contractId);
  const { cachedBCContractLines, cachedVariableContractLines } = useGetContractLinesFromCache(contractId);

  // User role check for Invoice/Reinvoice permissions
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);


  // Pause detection logic
  const hasSelectedLinesPaused = useMemo(() => {
    if (selectedLineRows.length === 0) return false;

    const bcLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
    const variableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const selectedLineIds = selectedLineRows.map(line => line.id);

    const selectedBcLines = bcLines.filter(line => line?.id && selectedLineIds.includes(line.id));
    const selectedVariableLines = variableLines.filter(line => line?.iD && selectedLineIds.includes(line.iD));

    const bcLinesPaused = selectedBcLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    const variableLinesPaused = selectedVariableLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    return bcLinesPaused || variableLinesPaused;
  }, [selectedLineRows, cachedBCContractLines, cachedVariableContractLines]);

  const hasPauseActive = hasSelectedLinesPaused;

  // Track whether we've loaded data at least once (to distinguish initial load from refetch)
  const hasLoadedOnceRef = useRef(false);
  const [isRefetching, setIsRefetching] = useState(false);

  // Update hasLoadedOnce when data is loaded
  useEffect(() => {
    if (billingCycles.length > 0 && !isLoadingBillingCycles) {
      hasLoadedOnceRef.current = true;
    }
  }, [billingCycles, isLoadingBillingCycles]);

  // Reset hasLoadedOnce when modal closes
  useEffect(() => {
    if (!open) {
      hasLoadedOnceRef.current = false;
      setIsRefetching(false);
    }
  }, [open]);

  // Compute loading states - initial load vs refetch
  const isInitialLoading = useMemo(() => {
    // Initial loading: data is loading AND we haven't loaded data once yet
    return (isContractLoading || isLoadingBillingCycles) && !hasLoadedOnceRef.current;
  }, [isContractLoading, isLoadingBillingCycles]);

  // Track refetching state
  useEffect(() => {
    if (hasLoadedOnceRef.current && isLoadingBillingCycles) {
      setIsRefetching(true);
    } else if (!isLoadingBillingCycles) {
      setIsRefetching(false);
    }
  }, [isLoadingBillingCycles]);

  // Fetch billing cycles when selected lines change
  useEffect(() => {
    if (selectedLineRows.length > 0 && open) {
      getBillingCycles({
        filter: {
          contractLineID: {
            containsValues: selectedLineRows.map((cl) => cl.id),
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLineRows, open]);

  // Transform Pidget data to Evo format
  const evoData = useMemo<BillingCyclesModalData>(() => {
    return buildEvoBillingData(
      billingCycles,
      contract,
      selectedLineRows,
      cachedBCContractLines,
    );
  }, [billingCycles, contract, selectedLineRows, cachedBCContractLines]);

  // Statuses that make a line read-only (should not be modified by save)
  const READ_ONLY_STATUSES = ['Cancelled', 'Expired', 'Terminated'];

  // Handle save from BillingCyclesHeader - calls the same mutation as BillingCadenceModal
  // Only sends non-read-only line IDs to prevent modifications on cancelled/expired/terminated lines
  const handleSave = useCallback((formData: {
    nextBcStartDate: string;
    reasonCode: string;
    comment: string;
  }) => {
    if (!formData.nextBcStartDate || !formData.reasonCode || !formData.comment.trim()) {
      return;
    }

    // Filter out read-only lines (Cancelled, Expired, Terminated) from the mutation
    const editableLineIds = selectedLineRows
      .filter((cl) => !READ_ONLY_STATUSES.includes(cl.payload?.contractLine?.status ?? ''))
      .map((cl) => cl.id);

    if (editableLineIds.length === 0) {
      return;
    }

    if (!isLoadingUpdateBillingCadence) {
      updateBillingCadence({
        contractId,
        contractLineIds: editableLineIds,
        billingCycleStartDate: toMutationDateString(formData.nextBcStartDate),
        changeReason: {
          name: formData.reasonCode,
          comment: formData.comment,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, selectedLineRows, updateBillingCadence, isLoadingUpdateBillingCadence]);

  // Don't render if not open
  if (!open) {
    return null;
  }

  return (
    <EvoWrapper>
      <BillingCyclesModal
        open={open}
        onOpenChange={onOpenChange}
        data={evoData}
        onSave={handleSave}
        isSaving={isLoadingUpdateBillingCadence}
        isLoading={isInitialLoading}
        isRefetching={isRefetching}
        onDataChanged={refetchBillingCycles}
        hasPauseActive={hasPauseActive}
        userRole={userRole}
      />
    </EvoWrapper>
  );
}

export default EvoBillingCyclesModal;
